'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

const steps = [
  {
    number: '01',
    title: 'Contact EarnestBridge',
    body: 'Reach out once you have a signed Letter of Intent. No application needed until your LOI is in hand.',
    time: 'LOI Signed',
  },
  {
    number: '02',
    title: 'Apply & Get Approved',
    body: 'Complete the EMD Program application and receive approval while negotiating your Purchase and Sale Agreement.',
    time: 'During PSA',
  },
  {
    number: '03',
    title: 'We Fund Your EMD',
    body: 'Once the PSA is signed, we wire your EMD directly to an approved title company. Your capital stays free.',
    time: 'PSA Signed',
  },
  {
    number: '04',
    title: 'Exercise EMD Option',
    body: 'Exercise your EMD Option before the Due Diligence Period expires. We only fund soft deposits and retain the right to terminate prior to the EMD going hard.',
    time: 'Before DD Expires',
  },
];

export default function HowItWorksPreview() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const els = entry.target.querySelectorAll('[data-reveal]');
            els.forEach((el, i) => {
              const element = el as HTMLElement;
              setTimeout(() => {
                element.style.opacity = '1';
                element.style.transform = 'translateY(0)';
                element.style.filter = 'blur(0)';
              }, i * 120);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      id="how-it-works-preview"
      className="py-28 px-6 relative z-10"
      style={{ background: 'var(--color-surface)' }}
      aria-label="How the EMD funding process works"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          data-reveal
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-20"
          style={{
            opacity: 0,
            transform: 'translateY(24px)',
            filter: 'blur(4px)',
            transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)',
          }}
        >
          <div>
            <p className="section-label mb-3">The Process</p>
            <h2
              id="hiw-preview-heading"
              className="font-display"
              style={{
                fontSize: 'clamp(2rem, 4vw, 3.5rem)',
                color: 'var(--color-foreground)',
                letterSpacing: '-0.02em',
                lineHeight: 1.05,
              }}
            >
              Four steps from
              <br />
              <span className="italic" style={{ color: 'var(--color-primary)' }}>
                inquiry to funded.
              </span>
            </h2>
          </div>
          <Link
            href="/emd-program"
            className="btn-outline text-[11px] font-bold uppercase tracking-[0.2em] px-7 py-3.5 rounded-full whitespace-nowrap"
          >
            Full Process Details →
          </Link>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, i) => (
            <div
              key={step.number}
              data-reveal
              className="relative"
              style={{
                opacity: 0,
                transform: 'translateY(28px)',
                filter: 'blur(6px)',
                transition: 'all 0.9s cubic-bezier(0.16,1,0.3,1)',
              }}
            >
              {/* Connector line (not on last) */}
              {i < steps.length - 1 && (
                <div
                  className="absolute top-8 left-full w-full h-px hidden lg:block"
                  style={{
                    background: 'linear-gradient(to right, var(--color-accent), transparent)',
                    width: 'calc(100% - 2rem)',
                    left: '60%',
                    zIndex: 0,
                  }}
                />
              )}

              <div
                className="relative z-10 p-8 rounded-2xl h-full"
                style={{
                  background: i === 2 ? 'var(--color-primary)' : 'var(--color-bg)',
                  border: i === 2 ? 'none' : '1px solid rgba(27,67,50,0.1)',
                }}
              >
                {/* Step number */}
                <div className="flex items-center justify-between mb-6">
                  <span
                    className="font-display font-bold"
                    style={{
                      fontSize: '3.5rem',
                      lineHeight: 1,
                      color:
                        i === 2 ? 'rgba(255,255,255,0.15)' : 'rgba(27,67,50,0.08)',
                    }}
                  >
                    {step.number}
                  </span>
                  <span
                    className="pill-badge"
                    style={{
                      background:
                        i === 2 ? 'rgba(82,183,136,0.25)' : 'rgba(82,183,136,0.1)',
                      color: i === 2 ? '#74C69D' : 'var(--color-accent)',
                    }}
                  >
                    {step.time}
                  </span>
                </div>

                <h3
                  className="font-display font-semibold text-xl mb-3"
                  style={{ color: i === 2 ? 'white' : 'var(--color-foreground)' }}
                >
                  {step.title}
                </h3>
                <p
                  className="text-sm leading-relaxed"
                  style={{
                    color: i === 2 ? 'rgba(255,255,255,0.65)' : 'var(--color-muted)',
                  }}
                >
                  {step.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}